import React, { useEffect, useRef, useState } from 'react';
import classes from './ProductionDateCard.module.scss';
import { getProductionDate } from '../../services';

// The makers autoasta's month-of-production catalogue covers (see
// Backend/app/utils/productionDate.js). Lexus and Infiniti JDM cars are
// catalogued under Toyota and Nissan.
const MAKERS = [
    { value: 'TOYOTA', label: 'Toyota / Lexus' },
    { value: 'NISSAN', label: 'Nissan / Infiniti' },
    { value: 'HONDA', label: 'Honda' },
    { value: 'MAZDA', label: 'Mazda' },
    { value: 'MITSUBISHI', label: 'Mitsubishi' },
    { value: 'SUBARU', label: 'Subaru' },
    { value: 'SUZUKI', label: 'Suzuki' },
    { value: 'DAIHATSU', label: 'Daihatsu' },
    { value: 'ISUZU', label: 'Isuzu' },
];
const ALIASES = { LEXUS: 'TOYOTA', INFINITI: 'NISSAN' };

const toMaker = (value) => {
    const upper = String(value || '').normalize('NFKC').trim().toUpperCase();
    const resolved = ALIASES[upper] || upper;
    return MAKERS.some((m) => m.value === resolved) ? resolved : '';
};

// Listings carry the model code only (makerDetail.type, sometimes with an
// emissions prefix such as "CBA-ZN6" or a "modified" suffix such as
// "JZX100カイ"); the frame number is that code plus a serial, so the code is
// pre-filled and the customer adds the serial from the auction sheet - or the
// whole number arrives from the sheet reader (chassisNo).
const toModelCode = (value) => {
    const code = (String(value || '').normalize('NFKC').trim().toUpperCase().split('-').pop().match(/^[A-Z0-9]+/) || [''])[0];
    return /^[A-Z0-9]{2,15}$/.test(code) ? code : '';
};

// Imports carry a 17-character VIN, which this Japanese-market catalogue
// cannot look up.
const isVin = (value) => /^[A-HJ-NPR-Z0-9]{17}$/.test(String(value || ''));

const MISS_MESSAGES = {
    not_found: 'This chassis number is not in the catalogue. Check the number and the maker, then try again.',
    // Usually the car is outside the catalogue's date range for its maker
    // (e.g. Hondas before Oct 1998); the range is shown with it.
    no_data: "The catalogue doesn't have a production date for this chassis number.",
    invalid: 'That chassis number was not recognised. Enter the model code, a hyphen, then the serial.',
};

const ProductionDateCard = ({ maker: listingMaker, chassisCode, chassisNo, onUpgradeRequired }) => {
    const modelCode = toModelCode(chassisCode);
    // Left blank for a maker the catalogue doesn't cover (Ferrari, Lamborghini, …)
    // rather than defaulting to the first entry, which claimed the car was a Toyota.
    const [maker, setMaker] = useState(() => toMaker(listingMaker));
    const [chassis, setChassis] = useState(modelCode ? `${modelCode}-` : '');
    const [fromSheet, setFromSheet] = useState(false);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState('');
    // Whether the customer has typed in the field - the sheet reader's number
    // never overwrites what they entered.
    const edited = useRef(false);

    // The detail page is reused when moving between cars.
    useEffect(() => {
        setMaker(toMaker(listingMaker));
        setChassis(modelCode ? `${modelCode}-` : '');
        setFromSheet(false);
        edited.current = false;
        setResult(null);
        setError('');
    }, [listingMaker, modelCode]);

    // The chassis number read off the auction sheet arrives after the page loads.
    useEffect(() => {
        if (chassisNo && !isVin(chassisNo) && !edited.current) {
            setChassis(chassisNo);
            setFromSheet(true);
        }
    }, [chassisNo]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!maker) {
            setError('Choose a maker — the catalogue covers Japanese makers only.');
            setResult(null);
            return;
        }
        const trimmed = chassis.trim();
        if (!/-\s*[A-Za-z0-9]/.test(trimmed)) {
            setError('Add the serial after the hyphen - it is printed on the auction sheet.');
            setResult(null);
            return;
        }
        setLoading(true);
        setError('');
        setResult(null);
        try {
            // axiosInterceptors resolves with the response body, and resolves
            // 4xx answers too: { data } on success, { message } otherwise.
            const body = await getProductionDate({ maker, chassis: trimmed });
            if (body?.data) {
                setResult(body.data);
            } else if (body?.upgradeRequired) {
                // The API reads the tier off the member's record, so this browser's is stale.
                onUpgradeRequired();
            } else {
                setError(body?.message || 'Something went wrong. Please try again.');
            }
        } catch (err) {
            setError(err.response?.data?.message || "We couldn't get an answer from the production date catalogue just now. Please try again in a minute.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={classes.card}>
            <h3 className={classes.title}>
                <i className="fas fa-industry"></i>
                Production Date
            </h3>
            <p className={classes.intro}>
                {fromSheet
                    ? "We've filled in the chassis number from the auction sheet - press Check to see the month this car was built."
                    : 'Check the month this car was built using its full chassis number from the auction sheet.'}
            </p>

            <form className={classes.form} onSubmit={handleSubmit}>
                <select
                    aria-label="Maker"
                    value={maker}
                    onChange={(e) => setMaker(e.target.value)}
                >
                    <option value="" disabled>Choose a maker</option>
                    {MAKERS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                </select>
                <input
                    aria-label="Chassis number"
                    type="text"
                    value={chassis}
                    onChange={(e) => {
                        edited.current = true;
                        setFromSheet(false);
                        setChassis(e.target.value);
                    }}
                    placeholder={`e.g. ${modelCode || 'JZX100'}-0012345`}
                    maxLength={40}
                    autoComplete="off"
                    spellCheck={false}
                />
                <button type="submit" disabled={loading}>
                    {loading ? 'Checking...' : 'Check'}
                </button>
            </form>

            {error && <div className={classes.error}>{error}</div>}

            {result && result.status === 'found' && (
                <div className={classes.result}>
                    <div className={classes.dateBox}>
                        <span className={classes.dateLabel}>Built</span>
                        <span className={classes.date}>{result.label}</span>
                        <span className={classes.chassis}>{result.chassis}</span>
                    </div>
                    {result.specs?.length > 0 && (
                        <dl className={classes.specs}>
                            {result.specs.map((spec) => (
                                <div key={spec.label} className={classes.specRow}>
                                    <dt>{spec.label}</dt>
                                    <dd>{spec.value}</dd>
                                </div>
                            ))}
                        </dl>
                    )}
                </div>
            )}

            {result && result.status !== 'found' && (
                <div className={classes.miss}>
                    <strong>{result.chassis}</strong>
                    <p>{MISS_MESSAGES[result.status] || MISS_MESSAGES.not_found}</p>
                    {result.coverage && result.status !== 'invalid' && (
                        <p>
                            It only covers {(MAKERS.find((m) => m.value === result.maker)?.label || result.maker).split(' / ')[0]} cars
                            built {result.coverage.from} to {result.coverage.to}, so this car may be outside that range.
                        </p>
                    )}
                </div>
            )}

            <p className={classes.note}>
                The month of production can differ from the date of first registration.
            </p>
        </div>
    );
};

export default ProductionDateCard;
